/**
 * Factory for Plotly `hovertemplate` strings.
 *
 * Survey of inline usage across `frontend/src/features/**` shows a small
 * number of recurring shapes that this factory covers:
 *  - bare value:        `%{y:.2f}<extra></extra>`
 *  - percent value:     `%{y:.1f}%<extra></extra>`
 *  - trace-name prefix: `%{fullData.name}: %{y:.2f}%<extra></extra>`
 *  - literal name:      `Real: %{y:.2f}%<extra></extra>`
 *  - date-prefixed:     `%{x|%d %b %Y}: %{y:.3f}<extra></extra>`
 *
 * This module is additive — existing call sites are intentionally left
 * unchanged. New call sites can opt-in via `hoverTemplate({...})`.
 */

export interface HoverTemplateOptions {
  /**
   * Trace name to prefix the value with.
   *  - `string` — emit a literal name (`"Real: ..."`)
   *  - `true`   — emit Plotly's `%{fullData.name}` placeholder
   *  - `false` / `undefined` — no name prefix
   */
  name?: string | boolean;
  /** d3-format spec for the y value. Default `.2f`. */
  format?: string;
  /** Unit suffix appended after the y value, e.g. `'%'`, `' bps'`. Default `''`. */
  suffix?: string;
  /** Prefix `%{x|%d %b %Y}: ` so the hover shows the date first. */
  showDate?: boolean;
}

const DEFAULT_FORMAT = '.2f';

export function hoverTemplate(opts: HoverTemplateOptions = {}): string {
  const { name, format = DEFAULT_FORMAT, suffix = '', showDate = false } = opts;

  const parts: string[] = [];

  if (showDate) {
    parts.push('%{x|%d %b %Y}: ');
  }

  if (typeof name === 'string' && name.length > 0) {
    parts.push(`${name}: `);
  } else if (name === true) {
    parts.push('%{fullData.name}: ');
  }

  parts.push(`%{y:${format}}${suffix}<extra></extra>`);

  return parts.join('');
}
